import { FunctionComponent, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './searchChildsDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { OptionsGroupDeprecated, SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { GET_PARENT_LIST } from '@/modules/API/parent/parentHandler'
import { Parent } from '@/types/Parent'
import { Child, ChildDaycare, ChildSection } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import Loading from '@/components/Loading/Loading'
import { NavigateFunction, useNavigate } from 'react-router-dom'

interface SearchChildsDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    childId: string
}

const SearchChildsDialog: FunctionComponent<SearchChildsDialogProps> = ({
    open = false,
    handleClose,
    daycareId,
    childId
}: SearchChildsDialogProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [parentList, setParentList] = useState<Parent[]>([])

    const {
        loading: loadingGetParents,
        data: dataGetParents,
        error: errorGetParents
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: daycareId
        }
    })

    useEffect(() => {
        if (dataGetParents) {
            setParentList(dataGetParents.getParentList)
        }
    }, [dataGetParents])

    useEffect(() => {
        if (errorGetParents) {
            handleClose()
        }
    }, [errorGetParents])

    function redirectToChildProfile(childId: string): void {
        navigate('/childs?child=' + childId)
        handleClose()
    }

    const selectOptions: SelectOptionsDeprecated = defineChildBySection(parentList, daycareId, t('withoutSection'))

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('searchChild')} />
            <DialogBody className={styles.DialogBody}>
                {loadingGetParents && <Loading overall={false} />}
                {errorGetParents && <ErrorMessage error={t('noChild')} />}
                {dataGetParents && (
                    <>
                        {selectOptions.length > 0 ? (
                            <div className={styles.InputContainer}>
                                <SelectUnique
                                    selectOptions={selectOptions}
                                    selectValue={childId}
                                    setSelectValue={redirectToChildProfile}
                                    className={styles.Select}
                                    label={t('childs')}
                                />
                            </div>
                        ) : (
                            <ErrorMessage className={styles.Text} error={t('noChild')} />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        </div>
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default SearchChildsDialog

function defineChildBySection(
    parentList: Parent[],
    daycareId: number,
    withoutSection: string
): SelectOptionsDeprecated {
    const mapBySection: Map<number, Child[]> = new Map()
    const sectionList: Map<number, ChildSection[]> = new Map()

    parentList.forEach((parent: Parent): void => {
        parent.childs.forEach((child: Child): void => {
            child.daycares
                .filter((daycare: ChildDaycare): boolean => daycare.daycareId === daycareId && daycare.visible)
                .forEach((daycare: ChildDaycare): void => {
                    if (daycare.sections.length === 0) {
                        const list: Child[] = mapBySection.get(0) ?? []
                        if (!list.some((c: Child): boolean => c.id === child.id)) list.push(child)
                        mapBySection.set(0, list)
                        return
                    }
                    daycare.sections.forEach((section: ChildSection): void => {
                        if (!sectionList.has(section.sectionId)) {
                            sectionList.set(section.sectionId, [section])
                        } else {
                            sectionList.get(section.sectionId)!.push(section)
                        }

                        const list: Child[] = mapBySection.get(section.sectionId) ?? []
                        if (!list.some((c: Child): boolean => c.id === child.id)) list.push(child)
                        mapBySection.set(section.sectionId, list)
                    })
                })
        })
    })

    const options: SelectOptionsDeprecated = []

    mapBySection.forEach((childs: Child[], sectionId: number): void => {
        const sectionFound: ChildSection[] | undefined = sectionList.get(sectionId)
        options.push({
            name: sectionFound ? sectionFound[0].sectionName : withoutSection,
            options: childs
                .sort((a: Child, b: Child): number => a.firstname.localeCompare(b.firstname))
                .map((child: Child): SelectOptionDeprecated => {
                    const complement: string | undefined = child.daycares.find(
                        (daycare: ChildDaycare): boolean => daycare.daycareId === daycareId
                    )?.complement
                    return {
                        value: child.id.toString(),
                        name: child.firstname + (complement ? ` - ${complement}` : '')
                    }
                })
        })
    })

    return options.sort((a: OptionsGroupDeprecated, b: OptionsGroupDeprecated): number => {
        const nameA: string = a.name ?? ''
        const nameB: string = b.name ?? ''

        if (nameA === withoutSection && nameB !== withoutSection) return 1
        if (nameB === withoutSection && nameA !== withoutSection) return -1

        return nameA.localeCompare(nameB)
    })
}
